import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  FormControlLabel,
  InputBase,
  SvgIcon,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Trans, useLingui } from "@lingui/react/macro";

import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import { tallyTokens } from "../../theme.tsx";
import axios from "../../utils/axios";
import { createPartnerCompany } from "./partnerApi";

/** A building, on the same 24px grid and weight as the rest of the set. */
const CompanyIcon: React.FC = () => (
  <SvgIcon
    viewBox="0 0 24 24"
    sx={{
      fontSize: 17,
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 1.7,
      strokeLinecap: "round",
      strokeLinejoin: "round",
    }}
  >
    <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 9h2a2 2 0 0 1 2 2v10M3 21h18M8 7h4M8 11h4M8 15h4" />
  </SvgIcon>
);

/** The draft's `.btn`: a hairline-bordered button, primary when filled. */
const buttonSx = (primary: boolean) => ({
  padding: "7px 13px",
  minWidth: 0,
  borderRadius: `${tallyTokens.radius.md}px`,
  fontSize: "13px",
  fontWeight: 600,
  lineHeight: 1.55,
  textTransform: "none" as const,
  boxShadow: "none",
  border: `1px solid ${primary ? tallyTokens.brand : tallyTokens.lineStrong}`,
  backgroundColor: primary ? tallyTokens.brand : "transparent",
  color: primary ? "#fff" : tallyTokens.ink,
  "&:hover": primary
    ? { backgroundColor: tallyTokens.brand, filter: "brightness(1.07)" }
    : { backgroundColor: tallyTokens.surface2 },
  "&.Mui-disabled": primary
    ? { backgroundColor: tallyTokens.brand, color: "#fff", opacity: 0.45 }
    : { color: tallyTokens.ink3 },
});

const agreementLabelSx = {
  alignItems: "flex-start",
  margin: 0,
  gap: "10px",
  "& .MuiFormControlLabel-label": {
    fontSize: "13.5px",
    lineHeight: 1.45,
    color: tallyTokens.ink,
  },
  "& a": {
    color: tallyTokens.brandInk,
    textDecoration: "underline",
    textUnderlineOffset: "2px",
    textDecorationColor: tallyTokens.lineStrong,
    "&:hover": { textDecorationColor: tallyTokens.brandInk },
  },
};

const checkboxSx = {
  padding: 0,
  marginTop: "1px",
  color: tallyTokens.lineStrong,
  "&.Mui-checked": { color: tallyTokens.brand },
  "& .MuiSvgIcon-root": { fontSize: 18 },
};

const alertSx = {
  mb: "14px",
  borderRadius: `${tallyTokens.radius.md}px`,
  backgroundColor: tallyTokens.critWash,
  color: tallyTokens.crit,
  fontSize: "13px",
  alignItems: "center",
  "& .MuiAlert-icon": { color: tallyTokens.crit, fontSize: 18 },
};

interface CreatePartnerCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}

const CreatePartnerCompanyDialog: React.FC<CreatePartnerCompanyDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useLingui();
  const theme = useTheme();
  const isDesktopScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [newCompanyName, setNewCompanyName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [dppAccepted, setDppAccepted] = React.useState(false);
  const [termsHash, setTermsHash] = React.useState<string | null>(null);
  const [dppHash, setDppHash] = React.useState<string | null>(null);
  const [termsUrl, setTermsUrl] = React.useState<string | null>(null);
  const [dppUrl, setDppUrl] = React.useState<string | null>(null);
  const [agreementsLoading, setAgreementsLoading] = React.useState(false);
  const [agreementsError, setAgreementsError] = React.useState<string | null>(
    null,
  );

  const resetDialogState = React.useCallback(() => {
    setNewCompanyName("");
    setCreateError(null);
    setTermsAccepted(false);
    setDppAccepted(false);
    setTermsHash(null);
    setDppHash(null);
    setTermsUrl(null);
    setDppUrl(null);
    setAgreementsError(null);
    setAgreementsLoading(false);
  }, []);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const fetchAgreements = async () => {
      setAgreementsLoading(true);
      setAgreementsError(null);
      try {
        const [termsRes, dppRes] = await Promise.all([
          axios.get("/v1/anonymous/terms_and_conditions/"),
          axios.get("/v1/anonymous/data_protection_policy/"),
        ]);
        setTermsHash(termsRes.data.agreement_hash);
        setTermsUrl(termsRes.data.content);
        setDppHash(dppRes.data.agreement_hash);
        setDppUrl(dppRes.data.content);
      } catch {
        setAgreementsError(
          t`Failed to load agreement documents. Please try again.`,
        );
      } finally {
        setAgreementsLoading(false);
      }
    };

    fetchAgreements();
  }, [open, t]);

  const handleClose = () => {
    onClose();
    resetDialogState();
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const response = await createPartnerCompany(
        newCompanyName.trim(),
        termsHash,
        dppHash,
      );
      if (!response.data) {
        setCreateError(t`Failed to create company.`);
        return;
      }
      await onCreated();
      handleClose();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setCreateError(t`You do not have permission to create companies.`);
      } else {
        setCreateError(t`Failed to create company.`);
      }
    } finally {
      setCreating(false);
    }
  };

  const canCreate =
    !creating &&
    !!newCompanyName.trim() &&
    termsAccepted &&
    dppAccepted &&
    termsHash !== null &&
    dppHash !== null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: "440px",
          margin: "16px",
          // Centred over the conversation rather than the whole window, so it
          // does not sit half behind the drawer.
          marginLeft: isDesktopScreen
            ? `calc(${SETTINGS.drawerWidth}px + 16px)`
            : "16px",
          padding: "22px 24px 20px",
          borderRadius: `${tallyTokens.radius.lg}px`,
          backgroundColor: tallyTokens.surface,
          border: `1px solid ${tallyTokens.line}`,
          boxShadow: tallyTokens.shadow.lg,
          backgroundImage: "none",
        },
      }}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(20, 15, 10, 0.4)" } },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "20px",
        }}
      >
        <Box
          sx={{
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            backgroundColor: tallyTokens.brandWash,
            color: tallyTokens.brandInk,
          }}
        >
          <CompanyIcon />
        </Box>
        <Box
          component="h2"
          sx={{
            margin: 0,
            fontSize: "18px",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
            color: tallyTokens.ink,
          }}
        >
          <Trans context="Partner portal button and dialog title for creating a company">
            Create Company
          </Trans>
        </Box>
      </Box>

      {createError && (
        <Alert severity="error" sx={alertSx}>
          {createError}
        </Alert>
      )}
      {agreementsError && (
        <Alert severity="error" sx={alertSx}>
          {agreementsError}
        </Alert>
      )}

      {/* Company name */}
      <Box
        component="label"
        htmlFor="create-partner-company-name"
        sx={{
          display: "block",
          marginBottom: "6px",
          fontSize: "12.5px",
          fontWeight: 600,
          color: tallyTokens.ink2,
        }}
      >
        <Trans context="Partner portal company name column and form label">
          Company Name
        </Trans>
      </Box>
      <InputBase
        id="create-partner-company-name"
        autoFocus
        fullWidth
        value={newCompanyName}
        onChange={(e) => setNewCompanyName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && canCreate) handleCreateCompany();
        }}
        sx={{
          fontSize: "15px",
          color: tallyTokens.ink,
          backgroundColor: tallyTokens.surface,
          border: `1px solid ${tallyTokens.lineStrong}`,
          borderRadius: `${tallyTokens.radius.md}px`,
          padding: "3px 12px",
          transition: "border-color 140ms ease, box-shadow 140ms ease",
          "&.Mui-focused": {
            borderColor: tallyTokens.brand,
            boxShadow: `0 0 0 3px ${tallyTokens.brandWash}`,
          },
        }}
      />

      {/* Agreements */}
      <Box
        sx={{
          marginTop: "16px",
          padding: "12px 14px",
          borderRadius: `${tallyTokens.radius.md}px`,
          backgroundColor: tallyTokens.canvas,
          border: `1px solid ${tallyTokens.line}`,
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          minHeight: "68px",
          justifyContent: "center",
        }}
      >
        {agreementsLoading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress size={20} sx={{ color: tallyTokens.brand }} />
          </Box>
        ) : (
          <>
            <FormControlLabel
              sx={agreementLabelSx}
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  sx={checkboxSx}
                />
              }
              label={
                <Trans context="Partner portal terms and conditions checkbox label">
                  I agree to the{" "}
                  <a
                    href={termsUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                </Trans>
              }
            />
            <FormControlLabel
              sx={agreementLabelSx}
              control={
                <Checkbox
                  checked={dppAccepted}
                  onChange={(e) => setDppAccepted(e.target.checked)}
                  sx={checkboxSx}
                />
              }
              label={
                <Trans context="Partner portal data protection policy checkbox label">
                  I agree to the{" "}
                  <a
                    href={dppUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Data Protection Policy
                  </a>
                </Trans>
              }
            />
          </>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "20px",
        }}
      >
        <Button
          onClick={handleClose}
          disabled={creating}
          disableRipple
          sx={buttonSx(false)}
        >
          <Trans context="Cancel button in partner create company dialog">
            Cancel
          </Trans>
        </Button>
        <Button
          onClick={handleCreateCompany}
          disabled={!canCreate}
          disableElevation
          sx={{ ...buttonSx(true), minWidth: "72px" }}
        >
          {creating ? (
            <CircularProgress size={16} sx={{ color: "#fff" }} />
          ) : (
            <Trans context="Submit button in partner create company dialog">
              Create
            </Trans>
          )}
        </Button>
      </Box>
    </Dialog>
  );
};

export default CreatePartnerCompanyDialog;
